import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, Bell, Trash2, Clock, CheckCircle2, LayoutGrid, List, Search } from 'lucide-react';
import { format } from 'date-fns';

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [users, setUsers] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reminder_type: 'client_followup',
    reminder_time: '',
  });

  useEffect(() => {
    fetchReminders();
    fetchUsers();
  }, []);

  const fetchReminders = async () => {
    try {
      const response = await salesApiClient.get('/reminders');
      setReminders(response.data);
    } catch (error) {
      toast.error('Failed to fetch reminders');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const getFilteredReminders = () => {
    return reminders.filter(reminder => {
      const matchesSearch = searchTerm === '' || 
        reminder.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reminder.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUser = filterUser === '' || reminder.user_id === filterUser;
      
      // Date filtering
      let matchesDate = true;
      if (filterDateFrom || filterDateTo) {
        const reminderDate = new Date(reminder.reminder_time || reminder.due_date);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          fromDate.setHours(0, 0, 0, 0);
          matchesDate = matchesDate && reminderDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDate = matchesDate && reminderDate <= toDate;
        }
      }
      
      return matchesSearch && matchesUser && matchesDate;
    });
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unknown';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/reminders', formData);
      toast.success('Reminder created successfully');
      setIsDialogOpen(false);
      setFormData({
        title: '',
        description: '',
        reminder_type: 'client_followup',
        reminder_time: '',
      });
      fetchReminders();
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reminder?')) return;
    try {
      await salesApiClient.delete(`/reminders/${id}`);
      toast.success('Reminder deleted');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleMarkDone = async (id) => {
    try {
      await salesApiClient.patch(`/reminders/${id}/mark-read`);
      toast.success('Reminder marked as done');
      fetchReminders();
    } catch (error) {
      toast.error('Failed to mark reminder as done');
    }
  };

  const handleSnooze = async (reminder, minutes) => {
    try {
      const newTime = new Date();
      newTime.setMinutes(newTime.getMinutes() + minutes);
      
      // Mark current as read
      await salesApiClient.patch(`/reminders/${reminder.id}/mark-read`);
      
      // Create new reminder with snoozed time
      await salesApiClient.post('/reminders', {
        title: reminder.title,
        description: reminder.description,
        reminder_type: reminder.reminder_type,
        reminder_time: newTime.toISOString().slice(0, 16),
      });
      
      toast.success(`Reminder snoozed for ${minutes} minutes`);
      fetchReminders();
    } catch (error) {
      toast.error('Failed to snooze reminder');
    }
  };

  const getReminderTypeColor = (type) => {
    switch (type) {
      case 'client_followup':
        return 'bg-blue-500/20 text-blue-400';
      case 'job_closing':
        return 'bg-green-500/20 text-green-400';
      case 'interview':
        return 'bg-purple-500/20 text-purple-400';
      case 'task':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getReminderTypeLabel = (type) => {
    return type.replace('_', ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const isPastDue = (reminderTime) => {
    return new Date(reminderTime) <= new Date();
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="reminders-title">
            Reminders
          </h1>
          <p className="text-base text-slate-400 mt-2">Manage your follow-ups and notifications</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-reminder-button">
              <Plus size={20} className="mr-2" />
              Add Reminder
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-reminder-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Reminder</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-slate-200">Reminder Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Follow up with CTO at Tech Corp"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="reminder-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description" className="text-slate-200">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Discuss contract renewal and new requirements"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="reminder-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reminder_type" className="text-slate-200">Reminder Type</Label>
                <select
                  id="reminder_type"
                  value={formData.reminder_type}
                  onChange={(e) => setFormData({ ...formData, reminder_type: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="reminder-type-select"
                >
                  <option value="client_followup">Client Follow-up</option>
                  <option value="job_closing">Job Closing</option>
                  <option value="interview">Interview</option>
                  <option value="task">Task Reminder</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="reminder_time" className="text-slate-200">Reminder Date & Time</Label>
                <Input
                  id="reminder_time"
                  type="datetime-local"
                  value={formData.reminder_time}
                  onChange={(e) => setFormData({ ...formData, reminder_time: e.target.value })}
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="reminder-time-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-reminder-button">
                Create Reminder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters & View Toggle */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input 
            placeholder="Search reminders..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
          />
        </div>
        <select
          value={filterUser}
          onChange={(e) => setFilterUser(e.target.value)}
          className="h-10 rounded-md border border-slate-700 bg-slate-800 text-white px-3 text-sm"
        >
          <option value="">All Sales Reps</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">From:</span>
          <Input 
            type="date" 
            value={filterDateFrom} 
            onChange={(e) => setFilterDateFrom(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">To:</span>
          <Input 
            type="date" 
            value={filterDateTo} 
            onChange={(e) => setFilterDateTo(e.target.value)}
            className="w-40 h-10 bg-slate-800 border-slate-700 text-white"
          />
        </div>
        {(filterUser || filterDateFrom || filterDateTo) && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              setFilterUser('');
              setFilterDateFrom('');
              setFilterDateTo('');
            }}
            className="text-slate-400 hover:text-white"
          >
            Clear Filters
          </Button>
        )}
        <div className="flex bg-slate-800 rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'ghost'} onClick={() => setViewMode('grid')}>
            <LayoutGrid size={18} />
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'ghost'} onClick={() => setViewMode('list')}>
            <List size={18} />
          </Button>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' && (
        <Card className="bg-slate-900 border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Title</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Type</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Due Date</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Assigned To</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Status</th>
                <th className="text-left p-4 text-sm font-medium text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {getFilteredReminders().map((reminder) => {
                const pastDue = isPastDue(reminder.reminder_time || reminder.due_date);
                return (
                  <tr key={reminder.id} className="border-t border-slate-800 hover:bg-slate-800/50">
                    <td className="p-4">
                      <div className="text-white font-medium">{reminder.title}</div>
                      <div className="text-slate-400 text-sm truncate max-w-xs">{reminder.description}</div>
                    </td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-1 rounded ${getReminderTypeColor(reminder.reminder_type)}`}>
                        {getReminderTypeLabel(reminder.reminder_type)}
                      </span>
                    </td>
                    <td className="p-4 text-slate-300">
                      {format(new Date(reminder.reminder_time || reminder.due_date), 'MMM dd, yyyy h:mm a')}
                    </td>
                    <td className="p-4 text-slate-400">{getUserName(reminder.user_id)}</td>
                    <td className="p-4">
                      {reminder.is_read ? (
                        <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-400">Done</span>
                      ) : pastDue ? (
                        <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-400">Due Now</span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">Pending</span>
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {!reminder.is_read && (
                          <Button variant="ghost" size="sm" onClick={() => handleMarkDone(reminder.id)}>
                            <CheckCircle2 size={16} className="text-green-400" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(reminder.id)}>
                          <Trash2 size={16} className="text-red-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {getFilteredReminders().length === 0 && (
            <div className="text-center py-12 text-slate-500">No reminders found</div>
          )}
        </Card>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
      <div className="space-y-4" data-testid="reminders-list">
        {getFilteredReminders().map((reminder) => {
          const pastDue = isPastDue(reminder.reminder_time || reminder.due_date);
          
          return (
            <Card
              key={reminder.id}
              className={`bg-slate-900 border rounded-xl shadow-sm p-6 ${
                pastDue ? 'border-amber-500/50' : 'border-slate-800'
              }`}
              data-testid={`reminder-card-${reminder.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`p-3 rounded-lg ${
                    pastDue ? 'bg-amber-500/20' : 'bg-blue-500/20'
                  }`}>
                    {pastDue ? (
                      <Bell size={24} className="text-amber-400" />
                    ) : (
                      <Clock size={24} className="text-blue-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white" data-testid="reminder-title">
                        {reminder.title}
                      </h3>
                      <span
                        className={`text-xs px-2 py-1 rounded font-medium ${
                          getReminderTypeColor(reminder.reminder_type)
                        }`}
                        data-testid="reminder-type"
                      >
                        {getReminderTypeLabel(reminder.reminder_type)}
                      </span>
                      {pastDue && (
                        <span className="text-xs px-2 py-1 rounded font-medium bg-amber-500/20 text-amber-400">
                          DUE NOW
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-400 mb-3" data-testid="reminder-description">
                      {reminder.description}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span data-testid="reminder-time">
                          {format(new Date(reminder.reminder_time), 'MMM dd, yyyy h:mm a')}
                        </span>
                      </div>
                      {reminder.is_read && (
                        <div className="flex items-center gap-1 text-green-400">
                          <CheckCircle2 size={14} />
                          <span>Acknowledged</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!reminder.is_read && pastDue && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkDone(reminder.id)}
                        className="hover:bg-green-500/10 text-green-400"
                        data-testid="mark-done-button"
                      >
                        <CheckCircle2 size={16} className="mr-1" />
                        Done
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSnooze(reminder, 15)}
                        className="hover:bg-blue-500/10 text-blue-400"
                        data-testid="snooze-15-button"
                      >
                        <Clock size={16} className="mr-1" />
                        15m
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSnooze(reminder, 60)}
                        className="hover:bg-blue-500/10 text-blue-400"
                        data-testid="snooze-60-button"
                      >
                        <Clock size={16} className="mr-1" />
                        1h
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(reminder.id)}
                    className="hover:bg-red-500/10"
                    data-testid="delete-reminder-button"
                  >
                    <Trash2 size={18} className="text-red-400" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
      )}

      {getFilteredReminders().length === 0 && reminders.length > 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center">
          <Bell size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No reminders match your filters.</p>
        </Card>
      )}

      {reminders.length === 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-reminders">
          <Bell size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No reminders set. Create your first reminder to stay on top of your tasks.</p>
        </Card>
      )}
    </div>
  );
}
